/**
 * The programme a module builder works inside, read-only.
 *
 * A builder edits their own modules in Module Studio; the programme's
 * structure belongs to its lead, so this states it rather than offering it.
 *
 * @module components/editor/OverviewSummary
 */

import React from "react";

import {
  Badge,
  countLabel,
  EmptyState,
  KeyValue,
  LinkButton,
  ListRow,
  PageHeader,
  Panel,
} from "@/components/ui";
import type { Programme } from "@/types";

export interface OverviewSummaryProps {
  programme: Programme;
  /** The modules this reader builds. */
  ownedModuleIds: ReadonlySet<string>;
  /** `studio`, under the editor's base. */
  studioPath: string;
}

export function OverviewSummary({
  programme,
  ownedModuleIds,
  studioPath,
}: OverviewSummaryProps): React.ReactElement {
  const modules = programme.modules ?? [];
  return (
    <div className="space-y-4" data-testid="overview-summary">
      <PageHeader
        title="Programme summary"
        level={2}
        description="This is the programme your modules belong to. Its lead maintains everything here."
        actions={
          <LinkButton to={studioPath} variant="primary" icon="puzzle-piece">
            Open Module Studio
          </LinkButton>
        }
      />
      <Panel title="Programme">
        <KeyValue
          entries={[
            { label: "Award", value: programme.awardType || "Not set" },
            { label: "NFQ level", value: programme.nfqLevel ?? "Not set" },
            {
              label: "Credits",
              // "0 credits" is a figure nobody has entered, beside two rows
              // that say "Not set" for the same absence.
              value: programme.totalCredits
                ? countLabel(programme.totalCredits, "credit")
                : "Not set",
            },
            { label: "Outcomes", value: (programme.plos ?? []).length },
          ]}
        />
      </Panel>
      <Panel title="Modules">
        {modules.length === 0 ? (
          <EmptyState
            title="No modules yet"
            description="Your Programme Lead adds the modules this programme is built from."
            data-testid="overview-summary-modules-empty"
          />
        ) : null}
        <div className="flex flex-col gap-2">
          {modules.map((module) => (
            <ListRow
              key={module.id}
              trailing={ownedModuleIds.has(module.id) ? <Badge tone="primary">Yours</Badge> : null}
              data-testid={`overview-summary-module-${module.id}`}
            >
              <span className="font-medium">{module.title || "Untitled module"}</span>
              {module.code ? <span className="ml-2 text-muted">{module.code}</span> : null}
            </ListRow>
          ))}
        </div>
      </Panel>
    </div>
  );
}
